import {
  buildTomorrowScheduleArray,
  extractCandidates,
  extractAttributes,
  extractProjects,
  assessLocationData,
  payrollFormat,
  filterCandidatesBySurface,
  DASHBOARD_SURFACE_FILTER_DEFAULT,
} from "./buildDashboardOpenings";
import { datesEqual } from "./dateCompare";
import { OFF_ATTRIBUTE_VALUE } from "./installerHasFullWeekOff";
import { isInstallerInactive } from "./installerInactive";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// buildCapacityReport (#668, Melissa) — the Next Day Openings window's "Weekly Openings" tab: for each
// office and each installer, how many of the week's crew-day OPENINGS are FILLED.
//
// The rules (Leland, 2026-10-14):
//   • FILLED  — the crew-day holds 1+ jobs, even when there's room for another.
//   • OPEN    — no job, and nothing that takes the day out.
//   • LEFT OUT (not an opening at all): OFF, BUFFER, a "Do Not Schedule" note,
//     compliance hold, an inactive installer, and Sundays.
//   A job always wins: a crew-day with a job counts as filled whatever else is on it.
//
// Pure, no React, no fetching — fed the same raw per-office payloads the Next Day
// Openings dashboard fetches (useDashboardOpenings), and it reads each day's cells
// through the dashboard's own builder (buildTomorrowScheduleArray), so the report,
// the dashboard and the grid read a cell the same way. Tests import this file.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/** Cell states. */
export const CELL_FILLED = "filled";
export const CELL_OPEN = "open";
export const CELL_LEFT_OUT = "leftOut";

// "Do Not Schedule" in any spelling — the same pattern as the FAV 2.0 notes rule's
// do_not_schedule group (predictive-scheduling shadow/crewNotes.js, PRED-033).
const DO_NOT_SCHEDULE_RE = /\bo not sch|\b(do ?n[o']?t|dont|don't)\b.*\bsch|\bdns\b|no schedul|no work per/i;

const normalizeNote = (t) => String(t == null ? "" : t).replace(/🔥/g, " ").replace(/\s+/g, " ").trim();

/**
 * Why a cell note takes the day out of the openings, or null when it doesn't.
 * "OFF" / "🔥OFF🔥" → off · BUFFER → buffer · Do Not Schedule (any spelling) → do_not_schedule.
 */
export function noteLeavesDayOut(note) {
  const t = normalizeNote(note);
  if (!t) return null;
  if (t.toUpperCase() === OFF_ATTRIBUTE_VALUE) return "off";
  if (/\bBUFFER\b/i.test(t)) return "buffer";
  if (DO_NOT_SCHEDULE_RE.test(t)) return "do_not_schedule";
  return null;
}

/** "M/D/YYYY" (the dashboard's date format) for a local Date. */
export const mdy = (d) => `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;

/** The 7 dates (Mon → Sun, "M/D/YYYY") of the week holding `anchor`. */
export function weekDatesFor(anchor = new Date()) {
  const a = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  const back = (a.getDay() + 6) % 7; // days since Monday
  const monday = new Date(a.getFullYear(), a.getMonth(), a.getDate() - back);
  return Array.from({ length: 7 }, (_, i) => mdy(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i)));
}

/** Number of weeks the Weekly Openings tab shows side by side (Leland, 2026-10-14). */
export const REPORT_WEEKS = 6;

/** `count` consecutive weeks (each Mon → Sun) starting with the week holding `anchor`. */
export function weeksFrom(anchor = new Date(), count = REPORT_WEEKS) {
  const a = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  return Array.from({ length: count }, (_, i) => weekDatesFor(new Date(a.getFullYear(), a.getMonth(), a.getDate() + 7 * i)));
}

/**
 * One installer's cell on one date: { state, reason }.
 * `scheduleArray` = buildTomorrowScheduleArray(...) for THAT date at the office.
 */
export function classifyReportCell({ scheduleArray, candidate, payrollNo, locationCode, dateString }) {
  const docs = Array.isArray(scheduleArray) ? scheduleArray : [];
  // 1. Any job on the day → filled (jobs carry no location: shared installers' jobs
  //    at other offices count, exactly as the grid shows "SHARED").
  if (docs.some((d) => d && d.attribute !== true && d.installer === payrollNo)) {
    return { state: CELL_FILLED, reason: null };
  }
  if (candidate && isInstallerInactive(candidate)) return { state: CELL_LEFT_OUT, reason: "inactive" };
  if (new Date(dateString).getDay() === 0) return { state: CELL_LEFT_OUT, reason: "sunday" };
  for (const d of docs) {
    if (!d || d.attribute !== true || d.installer !== payrollNo) continue;
    if (d.start && !datesEqual(d.start, dateString)) continue;
    // A compliance hold applies at every office (its doc carries the HOME office).
    if (d.complianceHold === true) return { state: CELL_LEFT_OUT, reason: "compliance_hold" };
    // Notes (OFF / BUFFER / …) are per office, as on the grid.
    if (d.location !== undefined && d.location !== locationCode) continue;
    const why = noteLeavesDayOut(d.encodedSmallTitle);
    if (why) return { state: CELL_LEFT_OUT, reason: why };
  }
  return { state: CELL_OPEN, reason: null };
}

/** Totals across the office rows that loaded cleanly; `partial` when any is loading or degraded. */
export function reportTotals(rows) {
  const ok = (rows || []).filter((r) => !r.loading && !r.degraded);
  const filled = ok.reduce((n, r) => n + r.filled, 0);
  const available = ok.reduce((n, r) => n + r.available, 0);
  return { filled, available, partial: ok.length !== (rows || []).length };
}

/** filled / available as a 0–1 fraction, or null when there are no openings at all. */
export const fillFraction = (filled, available) => (available > 0 ? filled / available : null);

/**
 * Heat-map band for an office-week (Leland 2026-10-28: "a heat map at the root level, with the
 * likely colors of red, yellow, green. A quick, at a glance look"). Share of the openings FILLED:
 * green ≥ 80%, yellow 50–79%, red under 50%; null when the week has no openings at all.
 */
export const HEAT_BANDS = { green: 0.8, yellow: 0.5 };
export function heatBand(fraction, bands = HEAT_BANDS) {
  if (fraction == null || !Number.isFinite(fraction)) return null;
  if (fraction >= bands.green) return "green";
  if (fraction >= bands.yellow) return "yellow";
  return "red";
}

/**
 * The heat-map colour, BLENDED (Leland 2026-10-28: "color shades/blending so that there are more
 * than just 3 static colors"): the hue slides red → yellow → green with the share filled —
 * solid red at 25% or less, yellow at 50%, solid green at 90% or more, shades in between.
 * → { bg: "hsl(…)", fg: "#000000" | "#ffffff" } (text colour picked for contrast), or null.
 */
export const HEAT_STOPS = [
  { at: 0.25, hue: 0 }, // red
  { at: 0.5, hue: 42 }, // yellow (the app's #FDB824)
  { at: 0.9, hue: 142 }, // green
];
export function heatHue(fraction, stops = HEAT_STOPS) {
  if (fraction == null || !Number.isFinite(fraction)) return null;
  if (fraction <= stops[0].at) return stops[0].hue;
  for (let i = 1; i < stops.length; i++) {
    const a = stops[i - 1];
    const b = stops[i];
    if (fraction <= b.at) return a.hue + ((fraction - a.at) / (b.at - a.at)) * (b.hue - a.hue);
  }
  return stops[stops.length - 1].hue;
}
const HEAT_S = 0.78;
const HEAT_L = 0.45;
/** HSL (h 0–360, s/l 0–1) → [r, g, b] 0–255. */
function hslToRgb(h, s, l) {
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0), f(8), f(4)].map((x) => Math.round(x * 255));
}
export function heatColor(fraction) {
  const hue = heatHue(fraction);
  if (hue == null) return null;
  const [r, g, b] = hslToRgb(hue, HEAT_S, HEAT_L);
  // WCAG relative luminance → black text on the light (yellow/lime) shades, white elsewhere.
  const lin = (c) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  // 0.179 = where black and white text have equal contrast.
  return { bg: `rgb(${r}, ${g}, ${b})`, fg: lum > 0.179 ? "#000000" : "#ffffff" };
}

const nameOf = (c) =>
  c.Name_Full || c.Calc_Name_Full || c.schedulingName || c.Calc_Installer_Payroll_Name || "(unknown)";

/**
 * One office's report.
 * @returns {{ locationCode, degraded, reason, filled, available, installers: Array<{ payrollNo, name, filled, available, days }> }}
 *   available = filled + open (left-out days are not openings); days = [{ date, state, reason }].
 */
export function buildLocationReport({ locationCode, entry, weekDates, surfaceFilter = DASHBOARD_SURFACE_FILTER_DEFAULT }) {
  const health = assessLocationData(entry);
  if (health.degraded) {
    return { locationCode, degraded: true, reason: health.reason, filled: 0, available: 0, installers: [] };
  }
  // The window's Soft / Hard / Both filter — the same one the Next Day Openings tab uses.
  const candidates = filterCandidatesBySurface(extractCandidates(entry.candidates), surfaceFilter);
  const attributes = extractAttributes(entry.attributes);
  const projects = extractProjects(entry.projects);
  const byDate = weekDates.map((dateString) => ({
    dateString,
    scheduleArray: buildTomorrowScheduleArray({ locationCode, candidates, attributes, projects, tomorrowDateString: dateString }),
  }));
  const seen = new Set();
  const installers = [];
  for (const c of candidates) {
    if (!c) continue;
    const payrollNo = payrollFormat(c.payrollNo);
    if (!payrollNo || seen.has(payrollNo)) continue;
    seen.add(payrollNo);
    const days = byDate.map(({ dateString, scheduleArray }) => ({
      date: dateString,
      ...classifyReportCell({ scheduleArray, candidate: c, payrollNo, locationCode, dateString }),
    }));
    const filled = days.filter((d) => d.state === CELL_FILLED).length;
    const open = days.filter((d) => d.state === CELL_OPEN).length;
    installers.push({ payrollNo, name: nameOf(c), filled, available: filled + open, days });
  }
  installers.sort((a, b) => Number(a.payrollNo) - Number(b.payrollNo) || a.payrollNo.localeCompare(b.payrollNo));
  return {
    locationCode,
    degraded: false,
    reason: null,
    filled: installers.reduce((n, i) => n + i.filled, 0),
    available: installers.reduce((n, i) => n + i.available, 0),
    installers,
  };
}

/**
 * One office over several weeks: the single-week report run per week, merged per
 * installer. → { locationCode, degraded, reason, weeks: [{ filled, available }],
 *   installers: [{ payrollNo, name, weeks: [{ filled, available, days }] }] }
 */
export function buildLocationWeeksReport({ locationCode, entry, weeks, surfaceFilter = DASHBOARD_SURFACE_FILTER_DEFAULT }) {
  const perWeek = weeks.map((weekDates) => buildLocationReport({ locationCode, entry, weekDates, surfaceFilter }));
  const first = perWeek[0] || { degraded: true, reason: "no-weeks", installers: [] };
  if (first.degraded) return { locationCode, degraded: true, reason: first.reason, weeks: [], installers: [] };
  const installers = first.installers.map((inst) => ({
    payrollNo: inst.payrollNo,
    name: inst.name,
    weeks: perWeek.map((w) => {
      const x = w.installers.find((i) => i.payrollNo === inst.payrollNo);
      // `days` (7 × { date, state, reason }) — the week opened day by day in the tab.
      return { filled: x ? x.filled : 0, available: x ? x.available : 0, days: x ? x.days : [] };
    }),
  }));
  return {
    locationCode,
    degraded: false,
    reason: null,
    weeks: perWeek.map((w) => ({ filled: w.filled, available: w.available })),
    installers,
  };
}

/** The multi-week report: one row per office (loading / degraded as in buildCapacityReport). */
export function buildWeeksReport({ locations, dataByLocation, weeks, surfaceFilter = DASHBOARD_SURFACE_FILTER_DEFAULT }) {
  return (locations || []).map((locationCode) => {
    const entry = dataByLocation && dataByLocation[locationCode];
    if (entry && entry.isLoading) return { locationCode, loading: true, weeks: [], installers: [] };
    return buildLocationWeeksReport({ locationCode, entry, weeks, surfaceFilter });
  });
}

/** Per-week totals across the clean office rows, plus `partial` when any office isn't clean. */
export function weeksTotals(rows, weekCount) {
  const ok = (rows || []).filter((r) => !r.loading && !r.degraded);
  const weeks = Array.from({ length: weekCount }, (_, i) => ({
    filled: ok.reduce((n, r) => n + ((r.weeks[i] && r.weeks[i].filled) || 0), 0),
    available: ok.reduce((n, r) => n + ((r.weeks[i] && r.weeks[i].available) || 0), 0),
  }));
  return { weeks, partial: ok.length !== (rows || []).length };
}

/**
 * The whole report: one row per office, in the order given. An office whose data is
 * still loading is { loading: true }; one whose data can't be trusted is degraded
 * (never counted as all-open — the #151 / #168 lesson).
 */
export function buildCapacityReport({ locations, dataByLocation, weekDates, surfaceFilter = DASHBOARD_SURFACE_FILTER_DEFAULT }) {
  return (locations || []).map((locationCode) => {
    const entry = dataByLocation && dataByLocation[locationCode];
    if (entry && entry.isLoading) return { locationCode, loading: true, filled: 0, available: 0, installers: [] };
    return buildLocationReport({ locationCode, entry, weekDates, surfaceFilter });
  });
}
